// Enhanced Supabase realtime + DB service for World Cup
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

let _client: ReturnType<typeof createClient> | null = null;

export function getSupabaseClient() {
  if (!_client && supabaseUrl && supabaseAnonKey &&
      !supabaseUrl.includes('dummy') && !supabaseAnonKey.includes('dummy')) {
    _client = createClient(supabaseUrl, supabaseAnonKey);
  }
  return _client;
}

export interface ScoreUpdatePayload {
  fixtureId: number;
  homeScore: number;
  awayScore: number;
  minute: number;
  status: string;
  homeName: string;
  awayName: string;
  homeTeamId?: number;
  awayTeamId?: number;
}

export interface EventUpdatePayload {
  fixtureId: number;
  type: string;
  player: string;
  team: string;
  minute: number;
  detail: string;
  assist?: string;
}

export interface GoalAnimationPayload {
  fixtureId: number;
  scorer: string;
  team: string;
  minute: number;
  isHome: boolean;
}

export type RealtimePayload = ScoreUpdatePayload | EventUpdatePayload;

const SCORE_CHANNEL = 'world-cup-scores';
const EVENT_CHANNEL = 'world-cup-events';
const GOAL_CHANNEL = 'world-cup-goals';

/** Broadcast a score update to all connected clients */
export async function broadcastScoreUpdate(payload: ScoreUpdatePayload) {
  const client = getSupabaseClient();
  if (!client) return;
  const channel = client.channel(SCORE_CHANNEL);
  await channel.send({ type: 'broadcast', event: 'score_update', payload });
}

/** Broadcast a match event to all connected clients */
export async function broadcastEventUpdate(payload: EventUpdatePayload) {
  const client = getSupabaseClient();
  if (!client) return;
  const channel = client.channel(EVENT_CHANNEL);
  await channel.send({ type: 'broadcast', event: 'event_update', payload });
}

/** Broadcast a goal animation to all connected clients */
export async function broadcastGoalAnimation(payload: GoalAnimationPayload) {
  const client = getSupabaseClient();
  if (!client) return;
  const channel = client.channel(GOAL_CHANNEL);
  await channel.send({ type: 'broadcast', event: 'goal_animation', payload });
}

/** Subscribe to score updates */
export function subscribeToScoreUpdates(
  onUpdate: (payload: ScoreUpdatePayload) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) return null;
  const channel = client
    .channel(SCORE_CHANNEL)
    .on('broadcast', { event: 'score_update' }, ({ payload }) => {
      onUpdate(payload as ScoreUpdatePayload);
    })
    .subscribe();
  return () => { client.removeChannel(channel); };
}

/** Subscribe to match event updates */
export function subscribeToEventUpdates(
  fixtureId: number,
  onUpdate: (payload: EventUpdatePayload) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) return null;
  const channel = client
    .channel(`${EVENT_CHANNEL}-${fixtureId}`)
    .on('broadcast', { event: 'event_update' }, ({ payload }) => {
      const p = payload as EventUpdatePayload;
      if (p.fixtureId === fixtureId) onUpdate(p);
    })
    .subscribe();
  return () => { client.removeChannel(channel); };
}

/** Subscribe to goal animations */
export function subscribeToGoalAnimations(
  onGoal: (payload: GoalAnimationPayload) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) return null;
  const channel = client
    .channel(GOAL_CHANNEL)
    .on('broadcast', { event: 'goal_animation' }, ({ payload }) => {
      onGoal(payload as GoalAnimationPayload);
    })
    .subscribe();
  return () => { client.removeChannel(channel); };
}

/** Subscribe to live_score_cache DB changes */
export function subscribeToLiveScoreDB(
  onUpdate: (row: Record<string, unknown>) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) return null;
  const channel = client
    .channel('live-score-db')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'live_score_cache' }, (payload) => {
      onUpdate(payload.new as Record<string, unknown>);
    })
    .subscribe();
  return () => { client.removeChannel(channel); };
}

/** Subscribe to standings_cache DB changes */
export function subscribeToStandingsDB(
  onUpdate: (row: Record<string, unknown>) => void
): (() => void) | null {
  const client = getSupabaseClient();
  if (!client) return null;
  const channel = client
    .channel('standings-db')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'standings_cache' }, (payload) => {
      onUpdate(payload.new as Record<string, unknown>);
    })
    .subscribe();
  return () => { client.removeChannel(channel); };
}

// ─── Favorite Teams ───────────────────────────────────────────────────────────

export interface FavoriteTeam {
  id: string;
  userId: string;
  teamId: number;
  teamName: string;
  teamLogo: string;
  teamCode: string;
}

export async function getFavoriteTeams(userId: string): Promise<FavoriteTeam[]> {
  const client = getSupabaseClient();
  if (!client) return [];
  const { data, error } = await client
    .from('favorite_teams')
    .select('*')
    .eq('user_id', userId);
  if (error) return [];
  return (data || []).map(r => ({
    id: r.id,
    userId: r.user_id,
    teamId: r.team_id,
    teamName: r.team_name,
    teamLogo: r.team_logo,
    teamCode: r.team_code,
  }));
}

export async function addFavoriteTeam(userId: string, team: { teamId: number; teamName: string; teamLogo: string; teamCode: string }): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;
  const { error } = await client.from('favorite_teams').insert({
    user_id: userId,
    team_id: team.teamId,
    team_name: team.teamName,
    team_logo: team.teamLogo,
    team_code: team.teamCode,
  });
  return !error;
}

export async function removeFavoriteTeam(userId: string, teamId: number): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;
  const { error } = await client
    .from('favorite_teams')
    .delete()
    .eq('user_id', userId)
    .eq('team_id', teamId);
  return !error;
}

// ─── Standings ────────────────────────────────────────────────────────────────

export interface StandingRow {
  groupName: string;
  teamId: number;
  teamName: string;
  teamLogo: string;
  teamCode: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  position: number;
}

export async function getStandings(): Promise<Record<string, StandingRow[]>> {
  const client = getSupabaseClient();
  if (!client) return {};
  const { data, error } = await client
    .from('standings_cache')
    .select('*')
    .order('group_name')
    .order('points', { ascending: false })
    .order('goal_difference', { ascending: false });
  if (error || !data) return {};
  const groups: Record<string, StandingRow[]> = {};
  data.forEach(r => {
    const row: StandingRow = {
      groupName: r.group_name,
      teamId: r.team_id,
      teamName: r.team_name,
      teamLogo: r.team_logo,
      teamCode: r.team_code,
      played: r.played,
      won: r.won,
      drawn: r.drawn,
      lost: r.lost,
      goalsFor: r.goals_for,
      goalsAgainst: r.goals_against,
      goalDifference: r.goal_difference,
      points: r.points,
      position: r.position,
    };
    if (!groups[r.group_name]) groups[r.group_name] = [];
    groups[r.group_name].push(row);
  });
  return groups;
}

// ─── Push Notifications ───────────────────────────────────────────────────────

export async function savePushSubscription(userId: string, sub: PushSubscription): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;
  const json = sub.toJSON();
  const { error } = await client.from('push_subscriptions').upsert({
    user_id: userId,
    endpoint: sub.endpoint,
    p256dh: json.keys?.p256dh || '',
    auth_key: json.keys?.auth || '',
  }, { onConflict: 'endpoint' });
  return !error;
}

export async function removePushSubscription(endpoint: string): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) return false;
  const { error } = await client.from('push_subscriptions').delete().eq('endpoint', endpoint);
  return !error;
}
